type SessionConfig = {
  authAuthority: string;
  inactivityMinutes?: number;
  heartbeatIntervalMinutes?: number;
  warnBeforeExpiryMinutes?: number;
  diagnosticsEnabled?: boolean;
  logoutUrl?: string;
};

const state = {
  cfg: null as SessionConfig | null,
  lastActivity: Date.now(),
  timer: null as number | null,
  started: false,
  expiring: false,
};

["mousemove", "keydown", "click", "scroll", "touchstart"].forEach((evt) =>
  document.addEventListener(
    evt,
    () => {
      state.lastActivity = Date.now();
    },
    { passive: true }
  )
);

async function loadConfig(): Promise<SessionConfig | null> {
  try {
    const res = await fetch("/api/session/config", { method: "GET", credentials: "same-origin" });
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as SessionConfig;
  } catch {
    return null;
  }
}

async function expireNow() {
  if (state.expiring) {
    return;
  }
  state.expiring = true;
  debugLog("Session marked as expired. Redirecting to /logout.");
  if (state.timer) {
    window.clearInterval(state.timer);
  }
  window.location.href = "/logout";
}

async function ping(authAuthority: string) {
  try {
    const res = await fetch(`${authAuthority}/api/access/heartbeat`, {
      method: "GET",
      headers: { "X-Heartbeat": "true" },
      credentials: "include",
    });

    debugLog("Heartbeat response received.", { status: res.status, authAuthority });

    if (res.status === 401) {
      if (state.timer) {
        window.clearInterval(state.timer);
      }
      debugLog("Heartbeat returned 401. Redirecting to /logout.");
      window.location.href = "/logout";
      return;
    }

    const cfg = state.cfg;
    if (!cfg) {
      return;
    }

    const inactivityMinutes = cfg.inactivityMinutes ?? 30;
    const inactivityMs = inactivityMinutes * 60 * 1000;
    const idleMs = Date.now() - state.lastActivity;
    debugLog("Heartbeat evaluated idle window.", { inactivityMinutes, idleMs });

    if (idleMs >= inactivityMs) {
      await expireNow();
      return;
    }
  } catch {
    debugLog("Heartbeat request failed due to network/runtime error.");
  }
}

export async function startSessionHeartbeat(): Promise<void> {
  if (state.started) {
    return;
  }
  state.started = true;

  const cfg = await loadConfig();
  if (!cfg?.authAuthority) {
    return;
  }

  state.cfg = cfg;
  debugLog("Session heartbeat config loaded.", cfg);

  const intervalMinutes = cfg.heartbeatIntervalMinutes ?? 5;
  const intervalMs = intervalMinutes * 60 * 1000;
  debugLog("Session heartbeat started.", { intervalMinutes, intervalMs });

  await ping(cfg.authAuthority);
  state.timer = window.setInterval(() => ping(cfg.authAuthority), intervalMs);
}

function debugLog(message: string, data?: unknown) {
  if (!state.cfg?.diagnosticsEnabled) {
    return;
  }

  if (typeof data === "undefined") {
    console.info(`[session-heartbeat] ${message}`);
    return;
  }

  console.info(`[session-heartbeat] ${message}`, data);
}
